import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import Dialog from '@mui/material/Dialog';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import CircularProgress from '@mui/material/CircularProgress';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import ArticleIcon from '@mui/icons-material/MenuBookTwoTone';
import FaqIcon from '@mui/icons-material/HelpOutlineTwoTone';
import VideoIcon from '@mui/icons-material/SmartDisplayTwoTone';
import SchoolIcon from '@mui/icons-material/SchoolTwoTone';
import SupportAgentIcon from '@mui/icons-material/SupportAgentTwoTone';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import InfoIcon from '@mui/icons-material/InfoOutlined';
import RichText from '../../../../hoc/RichText/RichText';
import Link from '../../Link/Link';
import { HelpDeskForm } from '../../../General/HelpDeskDialog';

const FOREST = '#2E5E4E';
const HEADER_BG = '#C8DBEE';
const HEADER_FG = '#38546D';

const isVideoUrl = (url) => /(?:youtube\.com|youtu\.be|vimeo\.com)/i.test(url || '');

// Normalize a YouTube/Vimeo url into an embeddable player url.
const toEmbedUrl = (url) => {
    if (!url) return null;
    const yt = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([\w-]{6,})/i);
    if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
    const vm = url.match(/vimeo\.com\/(?:video\/)?(\d+)/i);
    if (vm) return `https://player.vimeo.com/video/${vm[1]}`;
    return url;
};

const SECTION = (label) => (
    <Box sx={{ px: 2, py: 0.75, bgcolor: 'grey.50' }}>
        <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase">{label}</Typography>
    </Box>
);

/**
 * Fill in Confluence's "On this page" block.
 *
 * The contents macro is client-side: body.view ships an empty
 * <div class="toc-macro client-side-toc-macro"> carrying its configuration in
 * data attributes, and Confluence's own JavaScript populates it by walking the
 * page. We render the HTML statically, so without this the panel is an empty
 * box. Rebuild it from the article's own headings, honouring the macro's
 * configured heading levels and exclusion pattern.
 *
 * Anchors are emitted as data-kb-anchor rather than href="#id" on purpose: this
 * app is on a hash router, so a real fragment link would be read as a route
 * change and navigate away from the screen. HelpCenterDialog handles the click
 * and scrolls instead.
 */
function buildTableOfContents(doc) {
    const macro = doc.querySelector('.client-side-toc-macro, .toc-macro');
    if (!macro) return;

    const levels = (macro.getAttribute('data-headerelements') || 'H2,H3')
        .split(',').map(s => s.trim().toUpperCase()).filter(Boolean);

    let exclude = null;
    const pattern = macro.getAttribute('data-excludeheaderregex');
    if (pattern) {
        try { exclude = new RegExp(pattern); } catch { exclude = null; }   // author-supplied, may not compile
    }

    const headings = [...doc.querySelectorAll(levels.join(','))]
        .filter(h => !exclude || !exclude.test(h.textContent || ''))
        .filter(h => (h.textContent || '').trim().length > 0);

    if (!headings.length) {
        // Nothing to link — drop the whole panel rather than leave a blank box.
        (macro.closest('.panel') || macro).remove();
        return;
    }

    const list = doc.createElement('ul');
    list.className = 'kb-toc';
    headings.forEach((h, i) => {
        if (!h.id) h.id = 'kb-heading-' + i;
        const depth = Math.max(0, levels.indexOf(h.tagName));
        const li = doc.createElement('li');
        li.className = 'kb-toc-depth-' + depth;
        const a = doc.createElement('a');
        a.setAttribute('data-kb-anchor', h.id);
        a.textContent = (h.textContent || '').trim();
        li.appendChild(a);
        list.appendChild(li);
    });
    macro.appendChild(list);
}

const HelpCenterDialog = ({ open, onClose, tourAvailable = false, tourName, onTakeTour }) => {
    const { t, i18n } = useTranslation();
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const current = useSelector(state => state.help.current);

    // Prefer the backend `media[]` (typed kb/faq/video). Fall back to the
    // legacy `links` shape ({main, faqs}) so KB/FAQ articles still appear when
    // the backend hasn't been updated yet.
    const media = useMemo(() => {
        if (current?.media?.length) return current.media;
        const fromLinks = [];
        const main = current?.links?.main;
        if (main?.value || main?.documentId) {
            fromLinks.push({ id: 'main', label: main.label, url: main.value, documentId: main.documentId, type: isVideoUrl(main.value) ? 'video' : 'kb' });
        }
        (current?.links?.faqs || []).forEach((f, i) => {
            if (!f?.value && !f?.documentId) return;
            fromLinks.push({ id: `faq-${i}`, label: f.label, url: f.value, documentId: f.documentId, type: isVideoUrl(f.value) ? 'video' : 'faq' });
        });
        return fromLinks;
    }, [current]);
    // Name the screen after its main help article (the KB titles match the nav).
    // Without one, fall back from the definition's internal name (EquipmentCalibrationType)
    // to its translation under the camelCase key, else split the words.
    const mainLabel = current?.links?.main?.label;
    const screenName = useMemo(() => {
        if (mainLabel) return mainLabel;
        const name = current?.name;
        if (!name) return '';
        const key = name.charAt(0).toLowerCase() + name.slice(1);
        return i18n.exists(key) ? t(key) : name.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2');
    }, [mainLabel, current?.name, t, i18n]);

    const [active, setActive] = useState(null);          // selected left-rail item
    const [captureHidden, setCaptureHidden] = useState(false);

    // article reader state
    const [articleHtml, setArticleHtml] = useState(null);
    const [articleUrl, setArticleUrl] = useState(null);
    const [articleTitle, setArticleTitle] = useState(null);
    const [loadingArticle, setLoadingArticle] = useState(false);

    const groups = useMemo(() => ({
        kb: media.filter(m => m.type === 'kb'),
        faq: media.filter(m => m.type === 'faq'),
        video: media.filter(m => m.type === 'video')
    }), [media]);

    // Default selection when the dialog opens: first KB, else FAQ, else video,
    // else the tour, else the ticket form — never an empty reader pane.
    useEffect(() => {
        if (!open) return;
        setActive(prev => {
            if (prev) return prev;
            const first = groups.kb[0] || groups.faq[0] || groups.video[0];
            if (first) return { kind: first.type === 'video' ? 'video' : 'article', media: first };
            // Ticket submission is hidden until the feature rolls out, so the
            // empty-state falls through to the tour pane instead.
            // if (tourAvailable) return { kind: 'tour' };
            // return { kind: 'ticket' };
            return { kind: 'tour' };
        });
    }, [open, groups, tourAvailable]);

    // Reset selection each time the dialog is closed so the next open re-defaults.
    useEffect(() => { if (!open) setActive(null); }, [open]);


    const loadArticle = useCallback(async (item) => {
        const m = item.media;
        setArticleHtml(null); setArticleUrl(m.url); setArticleTitle(m.label);
        if (!m.documentId) return;                 // external link — show open-in-new-tab card
        setLoadingArticle(true);
        try {
            const result = await axios.get('/api/helparticle/' + m.documentId);
            if (result?.data?.description) {
                const parser = new DOMParser();
                const doc = parser.parseFromString(result.data.description, 'text/html');
                // Some articles open with a hand-rolled table of contents
                // (.fd-toc, or a bare leading <ul> of anchor links). Strip it
                // so the reader starts at the content. Confluence marks its own
                // contents block .toc-macro, and its articles routinely open
                // with a real list — removing the first <ul> unconditionally
                // deleted a paragraph of content from those.
                doc.querySelectorAll('.fd-toc').forEach(el => el.remove());
                const isConfluence = doc.querySelector('.confluenceTable, .confluence-information-macro, .toc-macro, .conf-macro') != null;
                if (!isConfluence) {
                    const firstUl = doc.querySelector('ul');
                    if (firstUl) firstUl.remove();
                }
                buildTableOfContents(doc);
                setArticleTitle(result.data.title || m.label);
                setArticleHtml(doc.body.innerHTML);
            }
        } catch {
            setArticleHtml(null);                  // fall back to the open-in-new-tab card
        } finally {
            setLoadingArticle(false);
        }
    }, []);

    useEffect(() => {
        if (active?.kind === 'article') loadArticle(active);
    }, [active, loadArticle]);

    // Scroll the reader to a contents entry's heading. Delegated from the
    // article wrapper because the markup is injected, not React-rendered.
    const onArticleClick = useCallback((e) => {
        const link = e.target.closest?.('[data-kb-anchor]');
        if (!link) return;
        e.preventDefault();
        const target = e.currentTarget.querySelector('#' + CSS.escape(link.getAttribute('data-kb-anchor')));
        if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, []);

    const select = (item) => setActive(item);
    const isSelected = (item) => {
        if (!active) return false;
        if (item.kind === 'tour') return active.kind === 'tour';
        if (item.kind === 'ticket') return active.kind === 'ticket';
        return active.media?.id === item.media?.id;
    };

    const launchTour = () => { onClose?.(); onTakeTour?.(); };

    const renderRailItem = (icon, label, item, onClick) => (
        <ListItemButton key={item.media?.id || item.kind} selected={isSelected(item)} onClick={onClick || (() => select(item))} sx={{ py: 1, px: 2 }}>
            <ListItemIcon sx={{ minWidth: 34 }}>{icon}</ListItemIcon>
            <ListItemText primary={label} primaryTypographyProps={{ variant: 'body2', noWrap: true }} />
        </ListItemButton>
    );

    const hasAnyMedia = media.length > 0 || tourAvailable;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullScreen={fullScreen}
            fullWidth
            maxWidth="lg"
            sx={{ visibility: captureHidden ? 'hidden' : 'visible' }}
            slotProps={{ paper: { sx: { borderRadius: fullScreen ? 0 : 2, height: fullScreen ? '100%' : '80vh' } } }}
        >
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2.5, py: 1.5, bgcolor: HEADER_BG }}>
                <Typography sx={{ fontFamily: 'Prompt', fontSize: '13pt', fontWeight: 600, color: HEADER_FG, textTransform: 'uppercase' }}>
                    {t('helpCenter')}{screenName ? ` · ${screenName}` : ''}
                </Typography>
                <IconButton size="small" onClick={onClose} sx={{ color: HEADER_FG }}><CloseIcon fontSize="small" /></IconButton>
            </Box>

            <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
                {/* Left rail */}
                <Box sx={{ width: { xs: 200, sm: 280 }, borderRight: '1px solid', borderColor: 'divider', overflowY: 'auto', flexShrink: 0 }}>
                    <List disablePadding>
                        {groups.kb.length > 0 && SECTION(t('knowledgeBase'))}
                        {groups.kb.map(m => renderRailItem(<ArticleIcon color="primary" fontSize="small" />, m.label, { kind: 'article', media: m }))}

                        {groups.faq.length > 0 && SECTION(t('faqs'))}
                        {groups.faq.map(m => renderRailItem(<FaqIcon sx={{ color: 'text.secondary' }} fontSize="small" />, m.label, { kind: 'article', media: m }))}

                        {groups.video.length > 0 && SECTION(t('videos'))}
                        {groups.video.map(m => renderRailItem(<VideoIcon sx={{ color: '#B4493C' }} fontSize="small" />, m.label, { kind: 'video', media: m }))}

                        {SECTION(t('tutorials'))}
                        {renderRailItem(
                            <SchoolIcon color={tourAvailable ? 'primary' : 'disabled'} fontSize="small" />,
                            tourAvailable ? (tourName || t('takeTour')) : t('guidedTour'),
                            { kind: 'tour' },
                            // One-click launch when a tour exists (matches the old Help menu);
                            // otherwise open the pane that explains none is available.
                            tourAvailable ? launchTour : () => select({ kind: 'tour' })
                        )}

                        {/* Submit a Ticket — hidden until the feature rolls out.
                        <Divider sx={{ my: 0.5 }} />
                        {renderRailItem(<SupportAgentIcon sx={{ color: FOREST }} fontSize="small" />, t('submitTicket'), { kind: 'ticket' })}
                        */}
                    </List>
                </Box>

                {/* Reader pane */}
                <Box sx={{ flex: 1, minWidth: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
                    {active?.kind === 'ticket' && (
                        <HelpDeskForm
                            showHeader={false}
                            showCancel={false}
                            onCaptureActiveChange={setCaptureHidden}
                            onSubmitted={() => { /* keep the Help Center open after submit */ }}
                        />
                    )}

                    {active?.kind === 'tour' && (
                        <Box sx={{ p: 4, textAlign: 'center', m: 'auto' }}>
                            <SchoolIcon color={tourAvailable ? 'primary' : 'disabled'} sx={{ fontSize: 48, mb: 1 }} />
                            <Typography variant="h6" gutterBottom>{tourAvailable ? (tourName || t('guidedTour')) : t('guidedTour')}</Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                {tourAvailable ? t('takeTourPrompt') : t('noTourForScreen')}
                            </Typography>
                            {tourAvailable && (
                                <Button variant="contained" startIcon={<SchoolIcon />} onClick={launchTour} sx={{ bgcolor: FOREST, '&:hover': { bgcolor: '#234B3E' } }}>
                                    {t('takeTour')}
                                </Button>
                            )}
                        </Box>
                    )}

                    {active?.kind === 'video' && (
                        <Box sx={{ p: 2 }}>
                            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5 }}>{active.media.label}</Typography>
                            <Box sx={{ position: 'relative', width: '100%', pt: '56.25%', borderRadius: 1.5, overflow: 'hidden', bgcolor: '#000' }}>
                                <Box component="iframe" src={toEmbedUrl(active.media.url)} title={active.media.label}
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen
                                    sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }} />
                            </Box>
                        </Box>
                    )}

                    {active?.kind === 'article' && (
                        <Box sx={{ p: 3 }}>
                            {loadingArticle ? (
                                <Stack alignItems="center" sx={{ py: 6 }}><CircularProgress size={28} /></Stack>
                            ) : articleHtml ? (
                                <>
                                    <Typography variant="h6" sx={{ mb: 1 }}>{articleTitle}</Typography>
                                    {articleUrl && <Box sx={{ mb: 2 }}><Link href={articleUrl} target="_blank"><strong>{t('fullHelpDocument')}</strong></Link></Box>}
                                    <Divider sx={{ mb: 2 }} />
                                    {/* Contents links carry data-kb-anchor rather than href="#id" —
                                        a real fragment would be swallowed by the hash router. */}
                                    <Box onClick={onArticleClick}>
                                        <RichText>{articleHtml}</RichText>
                                    </Box>
                                </>
                            ) : (
                                <Box sx={{ textAlign: 'center', py: 6 }}>
                                    <OpenInNewIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{articleTitle}</Typography>
                                    {articleUrl && (
                                        <Button variant="outlined" startIcon={<OpenInNewIcon />} onClick={() => window.open(articleUrl, '_blank')}>
                                            {t('openInNewTab')}
                                        </Button>
                                    )}
                                </Box>
                            )}
                        </Box>
                    )}

                    {!active && (
                        <Box sx={{ m: 'auto', p: 4, textAlign: 'center' }}>
                            <InfoIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                            <Typography variant="body2" color="text.secondary">
                                {hasAnyMedia ? t('selectTopic') : t('noHelpAvailable')}
                            </Typography>
                        </Box>
                    )}
                </Box>
            </Box>
        </Dialog>
    );
};

export default HelpCenterDialog;
